import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { getAttribution } from "@/lib/attribution";
import { pixelTrack } from "@/lib/pixel";
import { trackFunnel } from "@/lib/track";
import { Loader2, CheckCircle2, MessageCircle, ArrowRight } from "lucide-react";

const inputBase =
  "w-full rounded-xl bg-slate-950/60 border border-slate-700 px-4 py-3 text-[15px] text-foreground placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary transition-colors";

function visible(field, values) {
  const c = field.conditional;
  if (!c || !c.field_key) return true;
  const v = values[c.field_key];
  if (c.operator === "equals" || !c.operator) return v === c.value;
  if (c.operator === "not_equals") return v !== c.value;
  return true;
}

export default function LeadForm({ content, cta, testidPrefix = "lead" }) {
  const [fields, setFields] = useState(null);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState("form"); // form | connecting | success | error
  const [result, setResult] = useState(null);
  const [serverError, setServerError] = useState("");
  const startedRef = useRef(false);
  const lockRef = useRef(false);

  useEffect(() => {
    api.get("/public/form/default")
      .then((r) => setFields((r.data.fields || []).filter((f) => f.active).sort((a, b) => a.display_order - b.display_order)))
      .catch(() => setFields([]));
  }, []);

  const onFirstInput = () => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackFunnel("FORM_STARTED");
      pixelTrack("Contact");
    }
  };

  const setVal = (key, val) => {
    onFirstInput();
    setValues((v) => ({ ...v, [key]: val }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = (visFields) => {
    const errs = {};
    for (const f of visFields) {
      const val = (values[f.field_key] ?? "").toString().trim();
      if (f.required && !val) {
        errs[f.field_key] = "कृपया यह जानकारी भरें";
        continue;
      }
      if (!val) continue;
      if (f.type === "phone" && !/^[6-9]\d{9}$/.test(val)) errs[f.field_key] = "कृपया सही 10-अंकों का मोबाइल नंबर दर्ज करें";
      if (f.type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) errs[f.field_key] = "कृपया सही email दर्ज करें";
      if (f.type === "pincode" && !/^\d{6}$/.test(val)) errs[f.field_key] = "कृपया सही 6-अंकों का pincode दर्ज करें";
    }
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (lockRef.current || submitting) return;
    const visFields = fields.filter((f) => visible(f, values));
    const errs = validate(visFields);
    setErrors(errs);
    if (Object.keys(errs).length) return;

    lockRef.current = true;
    setSubmitting(true);
    setServerError("");
    trackFunnel("FORM_SUBMITTED");

    const data = {};
    visFields.forEach((f) => { if (values[f.field_key] != null && values[f.field_key] !== "") data[f.field_key] = values[f.field_key]; });

    try {
      const { data: res } = await api.post("/public/leads", {
        form_key: "default", data, attribution: getAttribution(),
      });
      // Lead conversion event ONLY after backend confirms creation.
      pixelTrack("Lead", { content_name: "Own Brand Loan Agency" });
      trackFunnel("FORM_SUBMITTED", { lead_number: res.lead_number });
      setResult(res);
      setState("connecting");
      setTimeout(() => {
        setState("success");
        if (res.whatsapp_url) {
          trackFunnel("WHATSAPP_CTA_CLICK", { lead_number: res.lead_number });
          window.open(res.whatsapp_url, "_blank");
        }
      }, 1600);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.fields) {
        setErrors(detail.fields);
        setServerError(detail.message || "कृपया form दोबारा जाँचें।");
      } else {
        setServerError(formatApiError(detail) || "Something went wrong. Please try again.");
      }
      setState("error");
    } finally {
      setSubmitting(false);
      lockRef.current = false;
    }
  };

  if (fields === null) {
    return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  if (state === "connecting" || state === "success") {
    return (
      <div className="text-center py-8 animate-fade-up" data-testid={`${testidPrefix}-success`}>
        <div className="mx-auto h-16 w-16 rounded-full bg-primary/15 flex items-center justify-center">
          {state === "connecting" ? <Loader2 className="h-8 w-8 text-primary animate-spin" /> : <CheckCircle2 className="h-8 w-8 text-primary" />}
        </div>
        <h3 className="mt-5 font-heading text-2xl font-bold">Thank You!</h3>
        <p className="mt-2 text-muted-foreground">आपकी enquiry successfully submit हो गई है।</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {state === "connecting" ? "हम आपको हमारी Business Advisory Team से connect कर रहे हैं…" : "WhatsApp पर हमारी टीम से जुड़िए।"}
        </p>
        {state === "success" && result?.whatsapp_url && (
          <a href={result.whatsapp_url} target="_blank" rel="noreferrer" data-testid={`${testidPrefix}-whatsapp-link`}
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-emerald-500 text-slate-950 font-semibold px-5 py-3 hover:bg-emerald-400 transition-colors">
            <MessageCircle className="h-5 w-5" /> WhatsApp पर जुड़ें
          </a>
        )}
      </div>
    );
  }

  const visFields = fields.filter((f) => visible(f, values));

  return (
    <form onSubmit={submit} className="space-y-3.5" data-testid={`${testidPrefix}-form`} noValidate>
      {visFields.map((f) => {
        const err = errors[f.field_key];
        const common = { id: f.field_key, "aria-invalid": !!err, "aria-label": f.label };
        return (
          <div key={f.field_key}>
            <label htmlFor={f.field_key} className="block text-sm font-medium mb-1.5 text-slate-200">
              {f.label} {f.required && <span className="text-primary">*</span>}
            </label>

            {["text", "email", "number", "phone", "city", "pincode", "date"].includes(f.type) && (
              <input
                {...common}
                type={f.type === "phone" || f.type === "pincode" ? "tel" : f.type === "city" ? "text" : f.type}
                inputMode={f.type === "phone" || f.type === "pincode" || f.type === "number" ? "numeric" : undefined}
                value={values[f.field_key] || ""}
                placeholder={f.placeholder || ""}
                onChange={(e) => setVal(f.field_key, e.target.value)}
                className={inputBase}
                data-testid={`${testidPrefix}-field-${f.field_key}`}
              />
            )}

            {f.type === "textarea" && (
              <textarea {...common} rows={3} value={values[f.field_key] || ""} placeholder={f.placeholder || ""}
                onChange={(e) => setVal(f.field_key, e.target.value)} className={inputBase}
                data-testid={`${testidPrefix}-field-${f.field_key}`} />
            )}

            {f.type === "dropdown" && (
              <select {...common} value={values[f.field_key] || ""} onChange={(e) => setVal(f.field_key, e.target.value)}
                className={inputBase} data-testid={`${testidPrefix}-field-${f.field_key}`}>
                <option value="">चुनें…</option>
                {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            )}

            {f.type === "radio" && (
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={f.label}>
                {(f.options || []).map((o) => (
                  <button key={o} type="button" onClick={() => setVal(f.field_key, o)}
                    data-testid={`${testidPrefix}-field-${f.field_key}-${o}`}
                    className={`rounded-xl border px-4 py-2.5 text-sm font-medium transition-colors ${
                      values[f.field_key] === o ? "border-primary bg-primary/15 text-primary" : "border-slate-700 text-slate-300 hover:border-slate-500"
                    }`}>
                    {o}
                  </button>
                ))}
              </div>
            )}

            {f.type === "checkbox" && (
              <div className="space-y-2">
                {(f.options || []).map((o) => {
                  const arr = values[f.field_key] || [];
                  const checked = arr.includes(o);
                  return (
                    <label key={o} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                      <input type="checkbox" checked={checked}
                        onChange={() => setVal(f.field_key, checked ? arr.filter((x) => x !== o) : [...arr, o])}
                        className="h-4 w-4 accent-emerald-500" />
                      {o}
                    </label>
                  );
                })}
              </div>
            )}

            {err && <p className="mt-1 text-xs text-red-400" data-testid={`${testidPrefix}-error-${f.field_key}`}>{err}</p>}
          </div>
        );
      })}

      {serverError && (
        <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid={`${testidPrefix}-server-error`}>
          {serverError}
        </div>
      )}

      <button type="submit" disabled={submitting} data-testid={`${testidPrefix}-submit`}
        className="w-full rounded-xl bg-primary text-primary-foreground font-bold px-5 py-3.5 hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-70 text-[15px]">
        {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <>{cta || content?.cta || "Submit"} <ArrowRight className="h-4 w-4" /></>}
      </button>
      <p className="text-center text-xs text-slate-500 leading-relaxed">
        {content?.form_subtext || "हमारी Business Advisory Team आपको Call करके पूरी जानकारी देगी।"}
      </p>
    </form>
  );
}
